import {
  Booking,
  DISCOUNT_PRODUCT_KEY,
  LegacyOrder,
  LegacyOrderLine,
  OrderRequest,
  SAME_DAY_STORAGE_DAY_PRODUCT_KEY,
  SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
  STORAGE_DAY_PRODUCT_KEY,
  STORAGE_HOUR_PRODUCT_KEY,
} from '@luggagehero/shared/interfaces';
import { SharedUtilProduct } from '@luggagehero/shared/util';

/** The drop-off cover offer's rules (AM-4924), as pure functions so each is tested on its own. */

/** Why a booking is not offered cover at drop-off. A string union so it narrows under every tsconfig. */
/** Why a booking is not offered cover: every reason the wizard does not run, and two of its own. */
export type DropoffCoverIneligibility = DropoffWizardIneligibility | 'no-payment-method' | 'covered';

export type DropoffCoverEligibility = { kind: 'eligible' } | { kind: 'ineligible'; reason: DropoffCoverIneligibility };

/** The order the booking is on now — the last of its order history, as the timeline reads it. */
export function currentOrder(booking: Booking | null | undefined): LegacyOrder | null {
  const orders = booking?.orderDetails;
  return orders && orders.length > 0 ? orders[orders.length - 1] : null;
}

/**
 * The order's cover lines — insurance lines the traveler is actually paying for.
 *
 * Not every insurance line is one. `SharedOrderService.convertNewOrder` appends a **synthetic** line to
 * every uncovered order (`optional: true, selected: false`) so the summary can show the cover that is
 * on offer, and a converted line with zero units is `selected: false` too. Counting those as cover
 * would read every uncovered booking as covered — exactly the booking this offer exists for.
 */
function insuranceLines(order: LegacyOrder | null | undefined): LegacyOrderLine[] {
  return (order?.orderLines ?? []).filter(
    (line) => SharedUtilProduct.isInsuranceFee(line.product) && line.selected !== false,
  );
}

/**
 * Whether the booking already carries cover. Either source is enough: the add-on flag the
 * booking was priced with, or an insurance line on the order it is on. Asking both is what keeps a
 * booking whose two records disagree from being offered — and charged — cover a second time.
 */
export function hasCover(booking: Booking): boolean {
  if (booking.price?.addOns?.insurance) {
    return true;
  }
  return insuranceLines(currentOrder(booking)).length > 0;
}

/** Why a booking does not get the drop-off wizard, and keeps the legacy drop-off sheet. */
export type DropoffWizardIneligibility =
  | 'walk-in'
  | 'guest'
  | 'partner'
  | 'not-confirmed'
  | 'no-order'
  | 'changed-since-order'
  | 'hand-bags';

export type DropoffWizardEligibility =
  | { kind: 'eligible' }
  | { kind: 'ineligible'; reason: DropoffWizardIneligibility };

/**
 * Whether this booking drops off through the drop-off wizard: a confirmed pre-booking. Walk-in, guest
 * and partner bookings keep the drop-off they have today. Walk-in and guest are each read from every
 * place they are recorded — the booking's add-ons, its metadata, and its current order's request —
 * because each surface that creates one has set a different subset over the years. The order's request
 * is the one the api treats as authoritative (it refuses a modify that changes it), and on many orders
 * it is the only one set. Partner is read from `readOnly`: the traveler api strips the booking's
 * `partner` record from every v3 response and sends `readOnly: true` in its place (AM-4892), so that
 * flag is the only sign of a partner booking the app ever sees.
 */
export function dropoffWizardEligibility(booking: Booking): DropoffWizardEligibility {
  const addOns = booking.price?.addOns;
  const orderAddOns = currentOrder(booking)?.orderRequest?.addOns;
  if (
    booking.isWalkIn ||
    addOns?.walkIn ||
    orderAddOns?.walkIn ||
    booking.metadata?.['lh_booking_category'] === 'walk_in'
  ) {
    return { kind: 'ineligible', reason: 'walk-in' };
  }
  if (booking.isGuest || addOns?.guest || orderAddOns?.guest || booking.metadata?.['lh_booking_category'] === 'guest') {
    return { kind: 'ineligible', reason: 'guest' };
  }
  if (booking.readOnly === true) {
    return { kind: 'ineligible', reason: 'partner' };
  }
  if (booking.status !== 'CONFIRMED') {
    return { kind: 'ineligible', reason: 'not-confirmed' };
  }
  // Every write the wizard makes is quoted from the booking and its order. A booking that has none, or
  // whose stay no longer says what its order says (a support edit), would be quoted from fields that
  // disagree, and the write would revert one of them. Those keep the legacy drop-off.
  if (!currentOrder(booking)?.orderRequest) {
    return { kind: 'ineligible', reason: 'no-order' };
  }
  if (!bookingMatchesOrder(booking)) {
    return { kind: 'ineligible', reason: 'changed-since-order' };
  }
  // The order counts every bag, but the api's modify writes the order's count as normal bags and keeps
  // the booking's hand bags, so any write would count hand bags twice. None are made today (0 confirmed
  // in production, 4 Oct 2026); any that exist keep the legacy drop-off.
  if ((booking.luggage?.hand ?? 0) > 0) {
    return { kind: 'ineligible', reason: 'hand-bags' };
  }
  return { kind: 'eligible' };
}

/**
 * Whether the drop-off wizard offers this booking cover: one it would run for at all, uncovered, with a
 * payment method to charge it to at pick-up, and still saying what its current order says.
 */
export function dropoffCoverEligibility(booking: Booking): DropoffCoverEligibility {
  const wizard = dropoffWizardEligibility(booking);
  if (wizard.kind === 'ineligible') {
    return wizard;
  }
  if (!booking.paymentMethodId) {
    return { kind: 'ineligible', reason: 'no-payment-method' };
  }
  if (hasCover(booking)) {
    return { kind: 'ineligible', reason: 'covered' };
  }
  return { kind: 'eligible' };
}

/**
 * Whether the booking still says what its current order says.
 *
 * Adding cover writes a new order built from the current one, and on a confirmed booking the api then
 * sets the booking's period, bags, pricing model and add-ons **from that order**. Some writes change a
 * confirmed booking without adding an order — a support edit, a bag count, a tip — and after one of
 * those, adding cover would quietly put the old stay back. So the offer is only made while the two
 * agree on the period, the bag count, and a tip the order does not carry. Measured on 1 Oct 2026: all
 * 342 uncovered, confirmed pre-bookings created since 1 Aug agreed, so this skips no one today.
 *
 * **Each date in the representation the app holds it in.** The api sends the period as wall-clock
 * digits and the app parses those as *local* time, while the order's dates arrive as instants whose
 * *UTC* digits are the wall clock (see {@link toWireDate}) — so the two are compared digit for digit,
 * never as instants. `checkIn`/`checkOut` are left out: the app converts them through the shop's
 * timezone, which would make the comparison depend on a timezone round trip this rule cannot check;
 * a support edit of only those (none in the measurement above) is the residual this does not cover.
 */
export function bookingMatchesOrder(booking: Booking): boolean {
  const request = currentOrder(booking)?.orderRequest;
  const period = booking.period;
  if (!request || !period) {
    return false;
  }
  const requestFrom = toWireDate(request.from);
  const requestTo = toWireDate(request.to);
  if (!requestFrom || requestFrom !== localWireDate(period.from) || requestTo !== localWireDate(period.to)) {
    return false;
  }
  const bags = (booking.luggage?.normal ?? 0) + (booking.luggage?.hand ?? 0);
  if (request.bags !== bags) {
    return false;
  }
  return !(booking.price?.addOns?.tip ?? 0);
}

/** A booking period date as the app holds it — wall-clock digits parsed as local time — in wire form. */
function localWireDate(value: unknown): string | null {
  const date = value instanceof Date ? value : null;
  if (!date || isNaN(date.getTime())) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    String(date.getFullYear()) +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    pad(date.getHours()) +
    pad(date.getMinutes())
  );
}

/** The quote API's date form: `YYYYMMDDHHmm`, no offset. */
const WIRE_DATE = /^\d{12}$/;

/**
 * A stored order date back in the form the quote API takes.
 *
 * The app holds the booking's order request with its dates **deserialized** (`Date`s), but
 * `request_quote` takes `YYYYMMDDHHmm` and answers 500 to anything else. Those digits are the
 * traveler's wall clock, stored *as if* UTC — so they come back as their UTC fields, which are the
 * exact digits the booking was quoted with in any timezone. Formatting in local time (what
 * `SharedUtilDate.serializeDate` does) would shift the stay by the device's offset, which is precisely
 * the re-pricing this flow must never do. Same output as `SharedUtilDate.serializeDateUtc`, written out
 * so this pure module does not pull moment into its consumers' tests.
 */
export function toWireDate(value: unknown): string | null {
  if (typeof value === 'string' && WIRE_DATE.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    String(date.getUTCFullYear()) +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes())
  );
}

/**
 * The booking's current order request as a fresh, sendable quote request — unchanged. A new object on
 * every call (`generateQuote` deletes a field off the request it is given).
 */
export function baselineRequest(booking: Booking): OrderRequest | null {
  const baseline = currentOrder(booking)?.orderRequest;
  if (!baseline) {
    return null;
  }
  const from = toWireDate(baseline.from);
  const to = toWireDate(baseline.to);
  if (!from || !to) {
    return null;
  }
  // A JSON round trip, not `structuredClone`: the request is plain data, and the jsdom test
  // environment has no `structuredClone`.
  const request = JSON.parse(JSON.stringify(baseline)) as Record<string, unknown>;
  // `request_quote` validates strictly and takes no nulls (a `discountCode: null` is a 400): a field
  // stored as null is sent as absent, which is what it means.
  Object.keys(request).forEach((key) => request[key] === null && delete request[key]);
  // Named, so the API keeps the booking's own storage product rather than choosing by today's date (AM-5020).
  return { ...(request as unknown as OrderRequest), from, to, bookingId: booking._id };
}

/**
 * The quote request that adds `tier` and changes nothing else: the booking's own current order
 * request, copied, with only `insurance` and `insuranceKey` set (and `bags`, below). Shop, period, pricing model,
 * discount code, optional products and every other add-on are the booking's, so the stay is not
 * re-priced and the free-cancellation pin does not move.
 *
 * Built here rather than through `SharedOrderService.getRequestForQuote`, which stamps the client's
 * *current* experiment variant onto the request and, for one variant, writes to the add-ons it was
 * handed — neither belongs to adding cover to a booking that already exists. Returns a fresh object every call — `generateQuote` deletes a field off the request it is
 * given, so a request must never be shared or reused.
 *
 * `bags`, when given, is the count the traveler confirmed at drop-off: cover and a changed bag count
 * go into one quote and one write, and cover is priced per bag.
 */
export function buildCoverRequest(booking: Booking, tier: 'basic' | 'premium', bags?: number): OrderRequest | null {
  const request = baselineRequest(booking);
  if (!request) {
    return null;
  }
  if (bags !== undefined) {
    request.bags = bags;
  }
  const context = SharedUtilProduct.insuranceContext(request.addOns);
  const insuranceKey = SharedUtilProduct.insuranceKeyForTier(context, tier);
  if (!insuranceKey) {
    return null;
  }
  request.addOns = { ...request.addOns, insurance: true, insuranceKey };
  return request;
}

/**
 * Each same-day storage product's advance twin (AM-4961, CTR-51). The api picks the twin by the drop-off
 * date and the request's `supportSameDayProducts`, never by price, so the same rate can arrive under
 * either key: a booking made the day before drop-off is stored with `dailyRate`, and a quote made on the
 * drop-off day may come back with `SAME_DAY_DAILY_RATE` at the same price, or the other way round.
 * The cover request is sent with the flag exactly as the order stored it: the flag says what this
 * client can render, and keeping a change to an advance booking off same-day pricing is the api's job
 * (AM-5020). Until then a cover re-quote can relabel the stay `SAME_DAY_*`, at parity a label only.
 */
const ADVANCE_KEY_BY_SAME_DAY_KEY: Readonly<Record<string, string>> = {
  [SAME_DAY_STORAGE_DAY_PRODUCT_KEY]: STORAGE_DAY_PRODUCT_KEY,
  [SAME_DAY_STORAGE_HOUR_PRODUCT_KEY]: STORAGE_HOUR_PRODUCT_KEY,
};

/** A storage key with its same-day label removed, so twins compare equal; every other key unchanged. */
function canonicalProductKey(key: string): string {
  return Object.prototype.hasOwnProperty.call(ADVANCE_KEY_BY_SAME_DAY_KEY, key)
    ? ADVANCE_KEY_BY_SAME_DAY_KEY[key]
    : key;
}

/**
 * A line's product and **rate**, for comparing two orders line by line.
 *
 * Quantity is deliberately left out. The request is copied from the order, so any difference in units is the
 * server's lifecycle rather than a re-price: an hourly ("pay as you go") booking is stored with zero
 * storage units — storage is counted at pick-up — while a fresh quote for the same request carries the
 * units of the requested period. What adding cover must never change is what each unit *costs*.
 *
 * A same-day storage product reads as its advance twin (AM-5019): which of the two the api labels the
 * line with depends on the day the quote is made, not on what the stay costs, and the unit price is
 * still compared, so a real re-price under either key is still caught.
 */
function lineSignature(line: LegacyOrderLine): string {
  return [canonicalProductKey(SharedUtilProduct.getProductKey(line.product)), line.unitPrice].join('|');
}

/** The key `SharedOrderService` gives the "Free cancellation" summary line it draws on the client. */
const FREE_CANCELLATION_SUMMARY_LINE_KEY = 'freeCancellation';

function isFreeCancellationSummaryLine(line: LegacyOrderLine): boolean {
  return SharedUtilProduct.getProductKey(line.product) === FREE_CANCELLATION_SUMMARY_LINE_KEY && !line.unitPrice;
}

/**
 * The lines whose rates adding cover must leave alone: everything but cover itself and the discount.
 *
 * The discount line is **derived**, not a rate: the api computes it from the storage amount alone
 * (`calculateDiscount`, which never sees insurance) and recomputes it at checkout (`completeOrder`) —
 * exactly like storage units. On an hourly booking, stored with zero storage units, it is therefore
 * zero on the booking and non-zero on any fresh quote, so comparing it would refuse cover to every
 * discounted hourly booking (about 7% of the uncovered pre-bookings in Aug–Sep 2026, nearly all on
 * "free hours" codes). The storage rate it derives from is still compared, and the code itself is
 * copied unchanged with the rest of the request.
 *
 * The free "Free cancellation" summary line is left out for the same kind of reason: the app draws it
 * itself (`SharedOrderService`), not the api, and where the shop prices a late-cancellation fee it is
 * drawn only while that order's own deadline is ahead. By drop-off the booking's pinned deadline has
 * usually passed, so its order has no line, while a fresh quote carries the api's provisional 15-minute
 * grace deadline and has one. Comparing it would refuse cover at almost every real drop-off. Only the
 * free line is skipped: a cancellation line that costs something is compared like any rate.
 */
function nonInsuranceSignature(order: LegacyOrder): string[] {
  return (order.orderLines ?? [])
    .filter(
      (line) =>
        !SharedUtilProduct.isInsuranceFee(line.product) &&
        SharedUtilProduct.getProductKey(line.product) !== DISCOUNT_PRODUCT_KEY &&
        !isFreeCancellationSummaryLine(line),
    )
    .map(lineSignature)
    .sort();
}

/** What applying a cover quote would do — see {@link checkCoverQuote}. */
export type CoverQuoteCheck =
  | { kind: 'ok'; unitPrice: number }
  | { kind: 'rejected'; reason: 'other-lines-changed' | 'no-single-cover-line' | 'wrong-tier' | 'price-changed' };

/**
 * Whether a cover quote adds the chosen cover and nothing else, checked *before* it is
 * applied: the same non-insurance lines at the same rates as the booking's current order, exactly one
 * insurance line, for the tier asked for, at the price the traveler was shown.
 *
 * A quote that fails is never applied. The likeliest cause is a rate that changed since booking —
 * a fresh quote prices storage at today's rates — and adding cover must not re-price the stay.
 */
export function checkCoverQuote(
  current: LegacyOrder,
  quote: LegacyOrder,
  expectedKey: string,
  shownUnitPrice: number,
): CoverQuoteCheck {
  const before = nonInsuranceSignature(current);
  const after = nonInsuranceSignature(quote);
  if (before.length !== after.length || before.some((signature, i) => signature !== after[i])) {
    return { kind: 'rejected', reason: 'other-lines-changed' };
  }
  const cover = insuranceLines(quote);
  if (cover.length !== 1) {
    return { kind: 'rejected', reason: 'no-single-cover-line' };
  }
  if (SharedUtilProduct.getProductKey(cover[0].product) !== expectedKey) {
    return { kind: 'rejected', reason: 'wrong-tier' };
  }
  if (cover[0].unitPrice !== shownUnitPrice) {
    return { kind: 'rejected', reason: 'price-changed' };
  }
  return { kind: 'ok', unitPrice: cover[0].unitPrice };
}

/**
 * The tier the booking actually carries after cover was added, read off its order — or `null` when it
 * does not carry exactly one insurance line. What the confirmation and the funnel report, rather than
 * what was asked for, so a server-side substitution cannot be reported as the tier the traveler chose.
 */
export function appliedCoverTier(booking: Booking): 'basic' | 'premium' | null {
  const cover = insuranceLines(currentOrder(booking));
  if (cover.length !== 1) {
    return null;
  }
  return SharedUtilProduct.isPremiumInsuranceFee(cover[0].product) ? 'premium' : 'basic';
}
